<script lang="ts">
import { GuidesSchema } from '$lib/schema-org';
import GuideFooter from '../guide-footer.svelte';
import InlineCTA from '../InlineCTA.svelte';

const HeroImage = '/thoughtful_person.png'; // Using a specific image for all guides

const title = 'How much Social Security will I get if I make $60,000 / year?';
const description =
  'Understand how your Social Security benefits are calculated if you consistently earn $60,000 per year.';
const publishDate = new Date('2025-06-15T00:00:00+00:00'); // Current date
const updateDate = new Date('2026-09-17T00:00:00+00:00');

let schema: GuidesSchema = new GuidesSchema();
schema.url = 'https://ssa.tools/guides/60k-income';
schema.title = title;
schema.image = HeroImage;
schema.datePublished = publishDate.toISOString();
schema.dateModified = updateDate.toISOString();
schema.description = description; // Pass the description to the schema
schema.imageAlt =
  'Laptop with piggybank representing Social Security benefits for $60k income';
schema.tags = [
  'Middle Income',
  'Social Security',
  '$60000',
  'Benefits Calculation',
];
</script>

<svelte:head>
  <title>{title} | SSA.tools</title>
  <meta name="description" content={description} />
  <link rel="canonical" href={schema.url} />
  {@html schema.render()}
  {@html schema.renderSocialMeta()}
</svelte:head>

<div class="guide-page">
  <h1>{title}</h1>
  <p class="postdate">
    Published: {publishDate.toLocaleDateString()}<br />
    Updated: {updateDate.toLocaleDateString()}
  </p>

  <figure class="hero-image">
    <img
      src={HeroImage}
      width="512"
      height="512"
      alt="Laptop with a piggy bank, symbolizing financial planning"
    />
  </figure>

  <p>
    This guide provides an estimate of your potential Social Security benefits
    if you consistently earn $60,000 per year throughout your career.
    Understanding how your earnings translate into future benefits is crucial
    for retirement planning. We'll walk through the assumptions and calculations
    to help you get a clearer picture.
  </p>

  <h2>Assumptions for this Calculation</h2>
  <p>
    To calculate your estimated Social Security benefit, we'll make the
    following assumptions:
  </p>
  <ul>
    <li>
      You have
      <a href="https://www.ssa.gov/oact/progdata/retirebenefit1.html"
        >35 years of income</a
      > at the equivalent value of today's
      <strong>$60,000</strong> per year.
    </li>
    <li>
      After indexing, your <strong
        >Average Indexed Monthly Earnings (AIME)</strong
      >
      will be $60,000 / 12 = <strong>$5,000.00</strong>.
    </li>
  </ul>

  <h2>Calculating Your Primary Insurance Amount (PIA)</h2>
  <p>
    Your <strong>Primary Insurance Amount (PIA)</strong> is the benefit you
    would receive if you start collecting Social Security at your
    <a href="https://www.ssa.gov/oact/progdata/nra.html"
      ><strong>Normal Retirement Age</strong></a
    >. It's calculated using a
    <a href="https://www.ssa.gov/oact/cola/piaformula.html">formula</a> that
    applies "<strong>bend points</strong>" to your AIME. For 2025, the
    <a href="https://www.ssa.gov/oact/cola/bendpoints.html">bend points</a> are <strong>$1,226</strong> and <strong>$7,391</strong>.
  </p>
  <p>Here's how the calculation works for an AIME of $5,000.00:</p>
  <ul>
    <li>
      <strong>90% of the first $1,226:</strong>
      0.90 * $1,226 = <strong>$1,103.40</strong>
    </li>
    <li>
      <strong>32% of the amount over $1,226 up to $7,391:</strong>
      The amount between $1,226 and $5,000.00 is $5,000.00 - $1,226 = $3,774.00.
      0.32 * $3,774.00 = <strong>$1,207.68</strong>
    </li>
    <li>
      <strong>15% of the amount over $7,391:</strong>
      Since your AIME ($5,000.00) is not over $7,391, this portion is
      <strong>$0.00</strong>.
    </li>
  </ul>
  <p>
    <strong
      >Your estimated Primary Insurance Amount (PIA) = $1,103.40 + $1,207.68 +
      $0.00 = $2,311.08 per month.</strong
    >
  </p>

  <InlineCTA type="calculator" />

  <h2>When to Start Benefits</h2>
  <p>
    The PIA is the amount you would earn if you started benefits at your Normal
    Retirement Age. However, your actual benefit may be higher or lower based on
    when you choose to start receiving benefits.
  </p>
  <ul>
    <li>
      <strong>Starting Early:</strong> If you start benefits before your
      <strong>Normal Retirement Age</strong>
      (as early as age 62), your monthly benefit will be
      <a href="https://www.ssa.gov/benefits/retirement/planner/agereduction.html"
        ><strong>permanently reduced</strong></a
      >.
    </li>
    <li>
      <strong>Starting Later:</strong> If you delay starting benefits past your
      <strong>Normal Retirement Age</strong>
      (up to age 70), your monthly benefit will be
      <strong>permanently increased</strong>
      through
      <a href="https://www.ssa.gov/benefits/retirement/planner/delayret.html"
        ><strong>delayed retirement credits</strong></a
      >.
    </li>
  </ul>

  <h2>Get Your Personalized Result</h2>
  <p>
    This guide provides an estimate based on specific assumptions. For a
    personalized calculation based on your actual earnings record and specific
    filing date choices, visit the calculator at <a href="https://ssa.tools"
      >ssa.tools</a
    >.
  </p>

  <h2>Additional Resources</h2>

  <ul>
    <li>
      <a href="https://www.ssa.gov/oact/progdata/retirebenefit1.html"
        >Social Security Administration: Social Security Retirement Benefit
        Calculation</a
      >
    </li>
    <li>
      <a href="https://www.ssa.gov/oact/cola/piaformula.html"
        >Social Security Administration: Primary Insurance Amount</a
      >
    </li>
    <li>
      <a href="https://www.ssa.gov/oact/cola/bendpoints.html"
        >Social Security Administration: Benefit Formula Bend Points</a
      >
    </li>
    <li>
      <a href="https://www.ssa.gov/oact/progdata/nra.html"
        >Social Security Administration: Normal Retirement Age (NRA)</a
      >
    </li>
    <li>
      <a href="https://www.ssa.gov/benefits/retirement/planner/agereduction.html"
        >Social Security Administration: Retirement Age and Benefit
        Reduction</a
      >
    </li>
    <li>
      <a href="https://www.ssa.gov/benefits/retirement/planner/delayret.html"
        >Social Security Administration: Delayed Retirement Credits</a
      >
    </li>
  </ul>

  <GuideFooter />
</div>
